import React from 'react'
import moment from 'moment'
import { ScrollView, TouchableOpacity, TouchableWithoutFeedback, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Divider } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { Section } from '@components'
import { MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT } from '@src/config/momentFormat'
import { Sensor } from '@src/types'
import { NoSensorView } from './UserSettings/NoSensorView'

interface SensorSettingsProps {
  sensor?: Sensor | null
  onIconPress: () => void
  activateDisabled: boolean
  onActivateSensorPress: () => void
  onCalibrateSensorPress: () => void
  onGetMoreSensorsPress: () => void
}

const DAYS_UNTIL_EXPIRATION = 3
const INTEGRATE_NEW_SENSOR_TEXT = 'Integrate a New Sensor'
const GET_MORE_SENSORS_TEXT = 'Get More Sensors'

const getSensorExpirationText = (daysRemaining: number) => {
  if (daysRemaining <= 0) {
    return '-'
  }

  if (daysRemaining === 1) {
    return 'Expires today'
  }

  return `${daysRemaining} days`
}

export const SensorSettings = ({
  sensor,
  onIconPress,
  activateDisabled,
  onActivateSensorPress,
  onCalibrateSensorPress,
  onGetMoreSensorsPress,
}: SensorSettingsProps) => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()

  if (!sensor) {
    return (
      <NoSensorView
        activateDisabled={activateDisabled}
        onActivateSensorPress={onActivateSensorPress}
      />
    )
  }

  // On the last sensor day, the moment().diff call will return zero. We want to
  // show one to help the user understanding it's the last sensor day
  const daysRemaining = sensor.expirationTime
    ? moment(sensor.expirationTime).diff(moment(), 'days') + 1
    : 0

  const baselineAdjustment = sensor.baselineAdjustment || 0

  const isExpiring = daysRemaining <= DAYS_UNTIL_EXPIRATION

  return (
    <ScrollView
      contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
      style={styles.container}
    >
      <View style={styles.content}>
        <Section title="Active Sensor Type">
          <Text type="regular" style={styles.text}>
            {sensor.description}
          </Text>
        </Section>
        <Section title="Serial Number">
          <Text type="regular" style={styles.text}>
            {sensor.serialNumber || '-'}
          </Text>
        </Section>
        <Divider />
        <Section title="Activation Date">
          <Text type="regular" style={styles.text}>
            {sensor.activationTime
              ? moment(sensor.activationTime).format(MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT)
              : '-'}
          </Text>
        </Section>
        <Divider />
        <Section title="Days Until Expiration">
          <Text type="regular" style={isExpiring ? styles.expiringSensorText : styles.text}>
            {getSensorExpirationText(daysRemaining)}
          </Text>
          {isExpiring && <Icon name="warning" style={styles.expiringSensorIcon} />}
        </Section>
        <Divider />
        <Section title="Manual Calibration" leftStyle={styles.sensorInfoTitle}>
          <TouchableOpacity
            accessibilityLabel="Calibration Info"
            style={styles.sensorInfo}
            onPress={onIconPress}
          >
            <Icon name="info" style={styles.infoIcon} />
          </TouchableOpacity>
          <TouchableWithoutFeedback
            accessibilityLabel="Calibrate sensor"
            onPress={onCalibrateSensorPress}
            disabled={daysRemaining === 0}
          >
            <View style={styles.textContainer}>
              <Text type="regular">{baselineAdjustment}</Text>
            </View>
          </TouchableWithoutFeedback>
        </Section>
        <Divider />
      </View>
      <View style={styles.buttonsWrapper}>
        <Button
          type={isExpiring ? 'primary' : 'outline'}
          size="block"
          disabled={activateDisabled}
          onPress={onActivateSensorPress}
          accessibilityLabel={INTEGRATE_NEW_SENSOR_TEXT}
        >
          {INTEGRATE_NEW_SENSOR_TEXT}
        </Button>
        <Button
          accessibilityLabel={GET_MORE_SENSORS_TEXT}
          type="transparent"
          size="block"
          style={styles.getSensorsButton}
          onPress={onGetMoreSensorsPress}
        >
          {GET_MORE_SENSORS_TEXT}
        </Button>
      </View>
    </ScrollView>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    paddingTop: 16,
    backgroundColor: 'theme.background',
  },
  content: {
    flex: 1,
  },
  text: {
    flex: 1,
    textAlign: 'right',
  },
  expiringSensorText: {
    color: 'theme.warning.base',
  },
  expiringSensorIcon: {
    marginLeft: 8,
    color: 'theme.warning.base',
  },
  sensorInfoTitle: {
    flex: 0,
  },
  sensorInfo: {
    flex: 1,
  },
  infoIcon: {
    width: 20,
    height: 20,
  },
  textContainer: {
    justifyContent: 'center',
    width: 80,
    height: 40,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'theme.surface.base',
  },
  buttonsWrapper: {
    marginTop: 24,
    marginHorizontal: 16,
  },
  getSensorsButton: {
    marginTop: 8,
  },
})
